import React, { useState } from "react"
import { useLocation, useNavigate } from "react-router"
import { useTranslations } from "@/hooks/useTranslations"
import {
	Plus,
	FolderOpen,
	Users,
	UsersRound,
	Settings,
	Palette,
	ShieldCheck,
	Globe,
	Loader2,
	MessageSquare,
	FileStack,
	Scale,
} from "lucide-react"
import {
	Sidebar,
	SidebarHeader,
	SidebarContent,
	SidebarFooter,
	SidebarMenu,
	SidebarMenuItem,
	SidebarMenuButton,
	SidebarGroup,
	SidebarGroupContent,
	SidebarRail,
	useSidebar,
} from "@/components/ui/sidebar"
import { simplePost } from "@/lib/apiUtils"
import { useAuth } from "@/hooks/useAuth"
import type { ViewType } from "@/types/reports"
import { useAppRouter, sidebarIdToPath } from "@/hooks/useAppRouter"
import { useReportsStore } from "@/stores/reportsStore"
import { useLocaleStore } from "@/stores/localeStore"

export function AppSidebar() {
	const t = useTranslations()
	const { pathname } = useLocation()
	const router = useNavigate()
	const { navigate } = useAppRouter()
	const { state, isMobile, setOpenMobile } = useSidebar()
	const { generatingExpedientId } = useReportsStore()
	const [isCreatingPlec, setIsCreatingPlec] = useState(false)
	const { permissions } = useAuth()
	const hasPermission = (perm: string) => permissions.includes(perm)

	const isNavItemActive = (itemId: string): boolean => {
		if (itemId === "tenders") return pathname.startsWith("/tenders") || pathname.startsWith("/expedients") || pathname.startsWith("/reports")
		if (itemId === "tender-assistant") return pathname.startsWith("/tender-assistant")
		if (itemId === "procurement-templates") return pathname.startsWith("/procurement-templates")
		if (itemId === "public-tenders") return pathname.startsWith("/public-tenders")
		if (itemId === "admin") return pathname.startsWith("/admin")
		if (itemId === "brand") return pathname.startsWith("/brand")
		return pathname === sidebarIdToPath(itemId)
	}

	const handleNavigate = (viewId: string) => {
		navigate(viewId as ViewType)
		if (isMobile) {
			setOpenMobile(false)
		}
	}

	const handleNewPlec = async () => {
		if (generatingExpedientId || isCreatingPlec) return
		setIsCreatingPlec(true)
		try {
			const data = await simplePost<{ locale: string }, { sessionId: string }>(
				"/api/tender-assistant/session",
				{ locale: useLocaleStore.getState().locale },
			)
			router(`/tender-assistant/${data.sessionId}`)
			if (isMobile) setOpenMobile(false)
		} catch {
			// Fallback: just navigate to the history page
			router("/tender-assistant")
			if (isMobile) setOpenMobile(false)
		} finally {
			setIsCreatingPlec(false)
		}
	}

	// Flat navigation items — no groups
	const navItems = [
		{ id: "tender-assistant", label: t("navigation.plecs"), icon: MessageSquare, show: true },
		{ id: "tenders", label: t("navigation.elsMeusPlecs"), icon: FolderOpen, show: true },
		{ id: "procurement-templates", label: t("navigation.plantilles"), icon: FileStack, show: true },
		{ id: "public-tenders", label: t("navigation.licitacionsPubliques"), icon: Globe, show: true },
		{ id: "brand", label: t("navigation.brand"), icon: Palette, show: hasPermission("manage_templates") },
		{ id: "organization", label: t("navigation.organization") || "Organització", icon: UsersRound, show: hasPermission("manage_users") || hasPermission("manage_teams") },
		{ id: "settings", label: t("navigation.settings"), icon: Settings, show: true },
	].filter((item) => item.show)

	return (
		<Sidebar collapsible='icon' variant='inset'>
			{/* Header with Company Logo */}
			<SidebarHeader>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton size='lg' asChild className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'>
							<button onClick={() => handleNavigate("tenders")}>
								<div className='flex items-center justify-center rounded-lg overflow-hidden'>
									{state === "collapsed" ? (
										<img src='/OptimLogo.png' alt='Optim Logo' width={32} height={32} className='size-8' />
									) : (
										<div className='flex items-center gap-2'>
											<img src='/OptimGovLogo.png' alt='OptimGov Logo' width={140} height={32} className='h-8 w-auto' />
											<span className='inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-600/20'>
												BETA
											</span>
										</div>
									)}
								</div>
							</button>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarHeader>

			{/* Navigation Content */}
			<SidebarContent>
				{/* Primary Action Buttons */}
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							<SidebarMenuItem>
								<SidebarMenuButton
									tooltip={t("navigation.nouPlec")}
									onClick={handleNewPlec}
									className={`bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground font-medium ${generatingExpedientId || isCreatingPlec ? "opacity-50 cursor-not-allowed" : ""}`}
								>
									{isCreatingPlec ? <Loader2 className='h-4 w-4 animate-spin' /> : <Plus className='h-4 w-4' />}
									<span>{t("navigation.nouPlec")}</span>
								</SidebarMenuButton>
							</SidebarMenuItem>
							<SidebarMenuItem>
								<SidebarMenuButton
									tooltip={t("navigation.novaAvaluacio")}
									onClick={() => {
										router("/evaluations/new")
										if (isMobile) setOpenMobile(false)
									}}
									className='bg-zinc-700 text-white hover:bg-zinc-800 hover:text-white font-medium'
								>
									<Scale className='h-4 w-4' />
									<span>{t("navigation.novaAvaluacio")}</span>
								</SidebarMenuButton>
							</SidebarMenuItem>
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>

				{/* Flat nav items */}
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							{navItems.map((item) => (
								<SidebarMenuItem key={item.id}>
									<SidebarMenuButton isActive={isNavItemActive(item.id)} tooltip={item.label} onClick={() => handleNavigate(item.id)}>
										<item.icon className='h-4 w-4' />
										<span>{item.label}</span>
									</SidebarMenuButton>
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>

			{/* Footer with Copyright */}
			<SidebarFooter>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton size='sm' className='text-xs text-muted-foreground hover:text-foreground'>
							<span className='truncate'>OptimTech &copy; {new Date().getFullYear()}</span>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarFooter>

			<SidebarRail />
		</Sidebar>
	)
}
